/**
 * RPA Service - Robotic Process Automation for Council Submissions
 * 
 * Phase 1A: Clipboard-First Guided Submission
 * 
 * This service handles:
 * - Authority detection from PCN text
 * - Submission method selection
 * - Proof tracking and validation
 * - Submission history
 */

import { supabase } from './supabaseClient';
import { 
  IssuingAuthority, 
  SubmissionMethod,
  findAuthorityByText,
  getPrimarySubmissionMethod,
  ALL_AUTHORITIES
} from '../data/issuingAuthorities';

export type SubmissionType = 'transfer' | 'appeal' | 'representation';
export type SubmissionMethodType = 'pcnguard_email' | 'user_email' | 'portal' | 'post' | 'other';
export type ProofType = 'email_delivery' | 'screenshot' | 'reference_number' | 'user_attestation';
export type SubmissionStatus = 'submitted' | 'acknowledged' | 'accepted' | 'rejected' | 'unknown';

export interface Submission {
  id: string;
  organisation_id: string;
  ticket_id: string;
  submission_type: SubmissionType;
  method: SubmissionMethodType;
  proof_type?: ProofType;
  proof_url?: string;
  reference_number?: string;
  status: SubmissionStatus;
  submitted_at: string;
  submitted_by: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface SubmissionWizardState {
  step: number;
  authority?: IssuingAuthority;
  submissionMethod?: SubmissionMethod;
  submissionType: SubmissionType;
  proofType?: ProofType;
  proofUrl?: string;
  referenceNumber?: string;
  notes?: string;
}

/**
 * Detect issuing authority from ticket data
 */
export async function detectAuthority(ticketData: {
  issuing_authority?: string;
  issuer?: string;
  location?: string;
  pcn_number?: string;
  contravention_type?: string;
}): Promise<IssuingAuthority | undefined> {
  const searchText = [
    ticketData.issuing_authority,
    ticketData.issuer,
    ticketData.location,
    ticketData.pcn_number,
    ticketData.contravention_type
  ].filter(Boolean).join(' ');

  if (!searchText.trim()) return undefined;

  return findAuthorityByText(searchText);
}

/**
 * Get recommended submission method for an authority
 */
export function getRecommendedSubmissionMethod(authority: IssuingAuthority): SubmissionMethod | undefined {
  return getPrimarySubmissionMethod(authority);
}

/**
 * Create a new submission record
 */
export async function createSubmission(data: {
  ticket_id: string;
  submission_type: SubmissionType;
  method: SubmissionMethodType;
  proof_type?: ProofType;
  proof_url?: string;
  reference_number?: string;
  notes?: string;
}): Promise<Submission> {
  const { data: submission, error } = await supabase
    .from('submissions')
    .insert({
      ...data,
      status: 'submitted',
      submitted_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) throw error;
  return submission;
}

/**
 * Get all submissions for a ticket
 */
export async function getTicketSubmissions(ticketId: string): Promise<Submission[]> {
  const { data, error } = await supabase
    .from('submissions')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * Update submission status
 */
export async function updateSubmissionStatus(
  submissionId: string,
  status: SubmissionStatus,
  notes?: string
): Promise<void> {
  const { error } = await supabase
    .from('submissions')
    .update({ 
      status,
      notes: notes || undefined,
      updated_at: new Date().toISOString()
    })
    .eq('id', submissionId);

  if (error) throw error;
}

/**
 * Upload proof screenshot
 */
export async function uploadProofScreenshot(
  ticketId: string,
  file: File
): Promise<string> {
  const fileName = `${ticketId}/${Date.now()}-${file.name}`;
  
  const { error } = await supabase.storage
    .from('submission-proofs')
    .upload(fileName, file);

  if (error) throw error;

  const { data: { publicUrl } } = supabase.storage
    .from('submission-proofs')
    .getPublicUrl(fileName);

  return publicUrl;
}

/**
 * Get all authorities (for manual selection)
 */
export function getAllAuthorities(): IssuingAuthority[] {
  return ALL_AUTHORITIES;
}

